import React from 'react';
import { ResponsiveContainer, XAxis, YAxis, Tooltip, Rectangle, ScatterChart, Scatter, Cell } from 'recharts';
import { useState, useEffect, useCallback } from 'react';
import { BiFullscreen, BiExitFullscreen} from 'react-icons/bi';


const HeatmapChart = ({ heatmapData }) => {
    const [cellSize, setCellSize] = useState({ width: 30, height: 30 });
    const [minValue, setMinValue] = useState();
    const [maxValue, setMaxValue] = useState();
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [containerDimensions, setContainerDimensions] = useState({ width: 800, height: 400 });

    const data = (heatmapData || []).map(row => ({
        x: row.hour + 0.5,
        y: row.day_number + 0.40,
        value: row.hourly_infringements
    }));

    // Dynamic cell size calculation
    const updateCellSize = useCallback(() => {
        // Get current window dimensions for responsive calculation
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        
        // Estimate container dimensions based on fullscreen state and window size
        const containerWidth = isFullscreen ? windowWidth - 100 : Math.min(windowWidth * 0.8, 800);
        const containerHeight = isFullscreen ? windowHeight - 120 : 400;
        
        // Update container dimensions state
        setContainerDimensions({ width: containerWidth, height: containerHeight });
        
        // Calculate available space
        const availableWidth = (containerWidth - 100) / 24; // 24 hours
        const availableHeight = (containerHeight - 50) / 7;  // 7 days
        
        // Add some padding to prevent overlap
        const width = Math.floor(availableWidth * 0.85);
        const height = Math.floor(availableHeight * 0.85);
        
        setCellSize({
            width: Math.min(Math.max(width, 15), 50),  // min 15px, max 50px
            height: Math.min(Math.max(height, 25), 60) // min 25px, max 60px
        });
    }, [isFullscreen]);

    // Handle window resize
    useEffect(() => {
        updateCellSize();
        
        const handleResize = () => {
            // Add a small delay to prevent too frequent updates
            setTimeout(updateCellSize, 100);
        };
        
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [updateCellSize]);

    // Update cell size when fullscreen state changes
    useEffect(() => {
        updateCellSize();
    }, [isFullscreen, updateCellSize]);

    useEffect(() => {
        if (data.length > 0) {
            let min = Math.min(...data.map(d => d.value));
            let max = Math.max(...data.map(d => d.value));

            setMinValue(min);
            setMaxValue(max);
        }
    }, [data]);

    // Function to get color based on value
    const getColor = (value) => {
        if (value === 0) return "rgba(0, 0, 0, 0.04)";
        const ratio = (value - minValue) / (maxValue - minValue);
        const red = ratio < 0.5 ? Math.floor(510 * ratio) : 255;
        const green = ratio < 0.5 ? 255 : Math.floor(255 - 510 * (ratio - 0.5));
        return `rgb(${red}, ${green}, 0)`;
    };

    // Custom shape for heatmap cells
    const CustomCell = ({ cx, cy, value }) => (
        <Rectangle
            x={cx - cellSize.width / 2}
            y={cy - cellSize.height / 2}
            width={cellSize.width}
            height={cellSize.height - 1}
            fill={getColor(value)}
            className="cursor-pointer hover:opacity-80 transition-opacity"
        />
    );

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload?.length) {
            const { x, y, value } = payload[0].payload;
            const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
            return (
                <div style={{ backgroundColor: "rgba(0, 0, 0, 0.8)", color: "#fff", padding: "8px", borderRadius: "5px", fontSize: "12px" }}>
                    <p>{value} infringements on {dayNames[Math.floor(y)]} at {Math.floor(x)}:00</p>
                </div>
            );
        }
        return null;
    };

    const HeatmapLegend = ({ minValue, maxValue }) => {
        const safeMin = isNaN(minValue) ? 0 : minValue;
        const safeMax = isNaN(maxValue) ? 0 : maxValue;

        const lowColor = getColor(safeMin);
        const midColor = getColor((safeMin + safeMax) / 2);
        const highColor = getColor(safeMax);

        return (
            <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "30px",
                marginLeft: "10px",
                padding: "5px",
                borderRadius: "8px",
                position: "relative",
                bottom: "5px",
            }}>
                <span style={{ color: "white", fontSize: "12px", position: "absolute", top: "0px", left: "30px" }}>
                    {maxValue}
                </span>

                {/* Mid Value Label */}
                <span style={{ color: "white", fontSize: "12px", position: "absolute", top: "50%", left: "30px", transform: "translateY(-50%)" }}>
                    {Math.round((safeMin + safeMax) / 2)}
                </span>

                {/* Min Value Label */}
                <span style={{ color: "white", fontSize: "12px", position: "absolute", bottom: "0px", left: "30px" }}>
                    {minValue}
                </span>

                <div style={{
                    width: "15px",
                    height: "400px",
                    background: `linear-gradient(180deg, ${highColor}, ${midColor}, ${lowColor})`,
                    borderRadius: "5px",
                }} />
            </div>
        );
    };

    return (
        <div
            className={`heatmap-container ${isFullscreen ? 'fullscreen-container' : ''}`}
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                gap: '10px',
            }}
        >
            {/* Fullscreen Toggle Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                {/* <button
                    className='toggle-chart-button'
                    onClick={() => setIsFullscreen(prev => !prev)}
                    title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                    {isFullscreen ? (
                        <BiExitFullscreen size={24} color="white" />
                    ) : (
                        <BiFullscreen size={24} color="white" />
                    )}
                </button> */}
            </div>

            {/* Main Chart & Legend */}
            <div
                className="heatmap-content"
                style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    height: isFullscreen ? "calc(100vh - 120px)" : "400px"
                }}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
                        <XAxis
                            type="number"
                            dataKey="x"
                            domain={[0, 24]}
                            ticks={[0, 6, 12, 18, 23]}
                            tickFormatter={(value) => `${value}:00`}
                            stroke='#ffffff'
                            tick={{ fontSize: 14, fontWeight: "bold", fill: "#ffffff" }}
                        />
                        <YAxis
                            type="number"
                            dataKey="y"
                            domain={[0, 7]}
                            interval={0}
                            ticks={[0.5, 1.5, 2.5, 3.5, 4.5, 5.5, 6.5]}
                            tickFormatter={(value) => ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][Math.floor(value)]}
                            stroke='#ffffff'
                            tick={{ fontSize: 14, fontWeight: "bold", fill: "#ffffff" }}
                        />
                        <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ stroke: "black", strokeWidth: 1 }}
                            wrapperStyle={{ zIndex: 100 }}
                        />
                        <Scatter name="Heatmap" data={data} shape={<CustomCell />} />
                    </ScatterChart>
                </ResponsiveContainer>

                <HeatmapLegend minValue={minValue} maxValue={maxValue} />
            </div>
        </div>
    );
};

export default HeatmapChart;